import { Icon, ImageUploadBase } from '@components'
import { useTheme, useNavigation } from '@react-navigation/native'
import { Theme } from '@theme'
import React from 'react'
import { StyleSheet, View, TouchableOpacity } from 'react-native'
import { Image } from 'expo-image'
import { defaultImage } from '@assets'
import { useUpdateGhostProfile, useUpdateProfile } from '@hooks'

type BannerHeaderProps = {
  profileId?: string
  avatarUrl?: string
  isGhost?: boolean
  isSmall?: boolean
  isBig?: boolean
  isMe?: boolean
  // When set, picking an image calls this instead of updating an existing
  // profile — used while CREATING a profile (no id yet) so the chosen photo is
  // stored on the form and saved on submit.
  onImageSelect?: (uri: string) => void
}

export const ProfileImage = ({
  profileId,
  avatarUrl,
  isGhost,
  isMe = false,
  isBig = false,
  isSmall = false,
  onImageSelect,
}: BannerHeaderProps) => {
  const theme = useTheme()
  const styles = getStyle(theme)

  const navigation = useNavigation()
  const { mutate: updateGhostProfile } = useUpdateGhostProfile()
  const { mutate: updateProfile } = useUpdateProfile()

  const changeProfileImage = (imageUri: string) => {
    if (onImageSelect) {
      onImageSelect(imageUri)
      return
    }
    if (isGhost) {
      updateGhostProfile({ id: profileId!, avatar: { uri: imageUri } })
    } else {
      updateProfile({ avatar: { uri: imageUri } })
    }
  }

  // common content used by both wrappers
  const content = (
    <>
      <Image
        style={
          isBig ? styles.imageProfileBig : isSmall ? styles.imageProfileSmall : styles.imageProfile
        }
        source={avatarUrl ? { uri: avatarUrl } : defaultImage}
        cachePolicy="memory-disk"
        transition={200}
        contentFit="cover"
      />
      {isGhost && (
        <View style={styles.ghostContainer}>
          <Icon name="ghost" color={theme.colors.primary} size={theme.iconSize.extraSmall} />
        </View>
      )}
    </>
  )

  if (onImageSelect || (profileId && (isMe || isGhost))) {
    return (
      <ImageUploadBase onImageSelect={changeProfileImage} style={styles.imageContainer}>
        {content}
        <View style={styles.editPhotoButton}>
          <Icon name="editPen" size={20} color={theme.colors.greyBlue} />
        </View>
      </ImageUploadBase>
    )
  }

  return (
    <TouchableOpacity style={styles.imageContainer} disabled={!isGhost}>
      {content}
    </TouchableOpacity>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    imageContainer: {
      borderWidth: 2,
      borderColor: theme.colors.white,
      shadowColor: theme.colors.black,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 8,
      borderRadius: 79,
    },
    imageProfile: {
      width: 100,
      height: 100,
      borderRadius: 60,
    },
    imageProfileBig: {
      width: 150,
      height: 150,
      borderRadius: 75,
    },
    imageProfileSmall: {
      width: 75,
      height: 75,
      borderRadius: 40,
    },
    ghostContainer: {
      position: 'absolute',
      bottom: 0,
      right: 0,
      backgroundColor: '#edf4fd',
      padding: theme.spacing.xs,
      borderRadius: 20,
      borderWidth: 2,
      borderColor: theme.colors.white,
    },
    editPhotoButton: {
      position: 'absolute',
      top: 0,
      right: 0,
      backgroundColor: theme.colors.white,
      padding: theme.spacing.xs,
      borderRadius: 20,
      pointerEvents: 'none',
    },
  })
